import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Badge, Button, ErrorState, InnerPageHeader, SectionCard, SkeletonCard,
} from '@parsina/framework';
import {
  Fingerprint, Languages, Pencil, Plug, Printer, ScrollText,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import { gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import SystemState from '../../components/SystemState';
import { COUNTRY_GROUPS, FALLBACK_COUNTRY } from '../../constants/countryProfile';
import { COUNTRY_CHANGES_QUERY, COUNTRY_PROFILE_QUERY } from './queries';
import {
  CeilingNote, ColumnValue, CountryCode, EditorLabel, NotReadBadge, countryErrorMessage, useCountryName,
} from './shared';
import EditGroupDialog from './EditGroupDialog';
import HistorySection from './HistorySection';

const GROUP_ICONS = {
  locale: Languages, identity: Fingerprint, capabilities: Plug, print: Printer,
};

/**
 * One country's rules — Archetype C (Record), PM-0218.
 *
 * ① InnerPageHeader (back to the list; the code, whether the country has a row
 *   of its own, its last editor) · ② alerts: the editor not enabled on this
 *   deployment, the market ceiling, what `*` / a missing row means · ⑤ P2: the
 *   four groups of CONTRACT.md §2 and the history in the main column, the
 *   reading rules in the rail.
 *
 * A group is edited in its own dialog with a required note; the header renders
 * from the route, so the way back survives loading and failure.
 */
export default function CountryDetail({ country, navigate }) {
  const { t, formatDate } = useTranslation();
  useDocumentTitle('nav.title.country');
  const countryName = useCountryName();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [history, setHistory] = React.useState({
    loading: true, error: null, disabled: false, changes: [],
  });
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(COUNTRY_PROFILE_QUERY, { country })
      .then((d) => {
        if (!d?.countryProfile) throw new Error(t('countries.detail.error'));
        setData(d.countryProfile);
      })
      .catch((e) => {
        // Not a country key at all: a whole-page state, like a clinic that does not exist.
        if (e?.code === 'INVALID_COUNTRY') setNotFound(true);
        else setError(countryErrorMessage(e, t));
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [country, t]);

  // The history is its own region: it sits behind the editor's platform_admin
  // contract, which a deployment may not have enabled while the reads work.
  const loadHistory = React.useCallback(() => {
    setHistory((h) => ({ ...h, loading: true, error: null }));
    gql(COUNTRY_CHANGES_QUERY, { country, limit: 100 })
      .then((d) => setHistory({
        loading: false, error: null, disabled: false, changes: d?.countryProfileChanges || [],
      }))
      .catch((e) => setHistory({
        loading: false,
        disabled: e?.code === 'EDITOR_DISABLED',
        error: e?.code === 'EDITOR_DISABLED' ? null : countryErrorMessage(e, t),
        changes: [],
      }));
  }, [country, t]);

  React.useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  if (notFound) {
    return (
      <SystemState
        code="404"
        titleKey="countries.detail.notFound.title"
        adviceKey="countries.detail.notFound.advice"
        testId="country-not-found"
        actions={[{ key: 'back', label: t('countries.detail.back'), onClick: () => navigate('/countries') }]}
      />
    );
  }

  const row = data?.row || null;
  const merged = data?.merged || null;
  const fallback = data?.fallback || null;
  const deployment = data?.deployment;
  const isFallbackRow = country === FALLBACK_COUNTRY;
  const canEdit = !!row && !history.disabled && !history.loading;
  const shown = row || merged;

  // ② at most two, and none of them ever dropped: a country WITHOUT a row is
  // «no row of its own» (+ the market), and offers no edit, so whether the editor
  // is enabled does not apply to it; a country WITH a row is the editor's state
  // (+ the market). What `*` is lives in the rail beside the rules, not here.
  const alerts = [];
  if (data && !row && !isFallbackRow) {
    alerts.push(
      <Alert key="norow" variant="info" data-testid="country-alert-no-row">{t('countries.detail.alerts.noRow')}</Alert>,
    );
  } else if (history.disabled) {
    alerts.push(
      <Alert key="editor" variant="info" title={t('countries.detail.alerts.editorDisabledTitle')} data-testid="country-alert-editor-disabled">
        {t('countries.detail.alerts.editorDisabledBody')}
      </Alert>,
    );
  }
  if (deployment?.market === 'intl') {
    alerts.push(
      <Alert key="intl" variant="warning" title={t('countries.ceiling.intlTitle')} data-testid="country-alert-intl">
        {t('countries.ceiling.intlBody')}
      </Alert>,
    );
  }

  return (
    <div className="flex h-full flex-col bg-background" data-testid="country-detail">
      <InnerPageHeader
        title={countryName(country)}
        onBack={() => navigate('/countries')}
        backLabel={t('countries.detail.back')}
        status={data ? (
          <Badge variant={row ? 'outline' : 'warning'} data-testid="country-row-badge">
            {isFallbackRow
              ? t('countries.detail.fallbackRow')
              : (row ? t('countries.detail.ownRow') : t('countries.detail.noRow'))}
          </Badge>
        ) : undefined}
        meta={[
          <CountryCode key="code" code={country} />,
          shown?.updatedAt ? (
            <span key="updated" className="inline-flex items-center gap-1.5">
              {t('countries.detail.updated', { date: formatDate(shown.updatedAt) })}
              <EditorLabel by={shown.updatedBy} />
            </span>
          ) : null,
        ].filter(Boolean)}
        data-testid="country-header"
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto w-full max-w-[1440px]">
          {loading ? (
            <div
              className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]"
              data-testid="country-loading"
              data-state="loading"
              aria-busy="true"
            >
              <div className="space-y-5"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
              <SkeletonCard />
            </div>
          ) : error ? (
            <ErrorState
              title={t('countries.detail.error')}
              description={error}
              onRetry={() => load()}
              retryLabel={t('common.actions.retry')}
              data-testid="country-error"
            />
          ) : (
            <div
              className="transition-opacity"
              style={{ opacity: refreshing ? 0.45 : 1 }}
              aria-busy={refreshing || undefined}
              data-state="content"
            >
              {alerts.length ? <div className="mb-5 flex flex-col gap-3">{alerts}</div> : null}

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="flex min-w-0 flex-col gap-5">
                  {COUNTRY_GROUPS.map((group) => (
                    <GroupSection
                      key={group.key}
                      group={group}
                      row={row}
                      merged={merged}
                      fallback={fallback}
                      deployment={deployment}
                      canEdit={canEdit}
                      onEdit={() => setEditing(group.key)}
                    />
                  ))}
                  <HistorySection
                    loading={history.loading}
                    error={history.error}
                    disabled={history.disabled}
                    changes={history.changes}
                    onRetry={loadHistory}
                  />
                </div>
                <RulesRail isFallbackRow={isFallbackRow} />
              </div>
            </div>
          )}
        </div>
      </div>

      {row ? COUNTRY_GROUPS.map((group) => (
        <EditGroupDialog
          key={group.key}
          open={editing === group.key}
          onOpenChange={(o) => setEditing(o ? group.key : null)}
          group={group}
          country={country}
          row={row}
          fallback={fallback}
          deployment={deployment}
          onSaved={() => { load({ quiet: true }); loadHistory(); }}
        />
      )) : null}
    </div>
  );
}

/**
 * One of the contract's four groups. With a row of its own the STORED row is
 * shown — NULL as «پیرو *» / «هیچ» — so what the editor changes is what is on
 * screen; without one, the merge with `*` the country actually reads.
 */
function GroupSection({
  group, row, merged, fallback, deployment, canEdit, onEdit,
}) {
  const { t } = useTranslation();
  const titleId = `country-group-${group.key}-title`;
  const Icon = GROUP_ICONS[group.key] || ScrollText;
  const groupName = t(`countries.groups.${group.key}`);

  return (
    <SectionCard
      id={`group-${group.key}`}
      title={<span id={titleId}>{groupName}</span>}
      sub={t(`countries.groupHints.${group.key}`)}
      icon={Icon}
      data-testid={`country-group-${group.key}`}
    >
      {group.key === 'capabilities' ? (
        <p className="text-xs leading-5 text-muted-foreground" data-testid="country-ceiling-intro">{t('countries.ceiling.intro')}</p>
      ) : null}
      <dl className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2" aria-labelledby={titleId}>
        {group.fields.map((spec) => (
          <div key={spec.field} className="min-w-0" data-testid={`country-field-${spec.field}`}>
            <dt className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              {t(`countries.fields.${spec.field}`)}
              <NotReadBadge field={spec.field} />
            </dt>
            <dd className="mt-1 font-semibold text-foreground">
              <ColumnValue
                spec={spec}
                row={row || merged}
                fallback={row ? fallback : null}
                merged={!row}
                deployment={deployment}
              />
              {spec.field === 'insuranceCatalog' && (row || merged)?.insuranceCatalog ? (
                <Link
                  to={`/countries?view=insurers&catalog=${encodeURIComponent((row || merged).insuranceCatalog)}`}
                  className="ms-2 text-xs font-normal text-primary hover:underline"
                  data-testid="country-insurers-link"
                >
                  {t('countries.detail.seeInsurers')}
                </Link>
              ) : null}
            </dd>
            {spec.kind === 'capability' ? (
              <dd className="mt-0.5">
                <CeilingNote spec={spec} value={(row || merged)?.[spec.field]} deployment={deployment} />
              </dd>
            ) : null}
          </div>
        ))}
      </dl>
      {canEdit ? (
        <div className="mt-1 flex justify-end border-t border-border pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            aria-label={t('countries.detail.editAria', { group: groupName })}
            data-testid={`country-edit-${group.key}`}
          >
            <Pencil size={14} aria-hidden />
            {t('countries.detail.edit')}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
}

/** The reading rules, beside the page they govern — what NULL, empty and the ceiling mean. */
function RulesRail({ isFallbackRow }) {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('countries.rules.title')} icon={ScrollText} data-testid="country-rules">
      {/* What `*` is, where the rules are read — not an alert: it is not a condition to act on. */}
      {isFallbackRow ? (
        <p className="text-xs leading-6 text-foreground" data-testid="country-rules-fallback">
          {t('countries.detail.alerts.fallback')}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2.5 text-xs leading-6 text-muted-foreground">
        <li>{t('countries.rules.defaults')}</li>
        <li>{t('countries.rules.capabilities')}</li>
        <li>{t('countries.rules.ceiling')}</li>
        <li>{t('countries.rules.notes')}</li>
      </ul>
    </SectionCard>
  );
}
